import type { DataOption } from '@/shared/components/HookForm/types'
import type { CatalogParamModel } from '@/shared/generated'

import i18n from '@/shared/i18n/client'

const EMPTY = '—'

const labelFor = (options: DataOption[], value: string): string =>
	options.find((o) => o.value === value)?.label ?? value

type ParamValueOptions = {
	competitors?: DataOption[]
	priceColumns?: DataOption[]
}

/**
 * Render a param value for the read-only tactic view: competitor ids resolve to
 * names, booleans/aggregations to labels, empty values to an em dash.
 */
export const formatParamValue = (
	param: CatalogParamModel,
	value: unknown,
	{ competitors = [], priceColumns = [] }: ParamValueOptions = {}
): string => {
	if (value == null || value === '') return EMPTY

	switch (param.type) {
		case 'price_column':
			return labelFor(priceColumns, String(value))
		case 'boolean':
			return value
				? i18n.t('general_yes', { defaultValue: 'Yes' })
				: i18n.t('general_no', { defaultValue: 'No' })
		case 'competitor_list': {
			const ids = Array.isArray(value) ? value : []
			if (!ids.length) return EMPTY

			return ids.map((id) => labelFor(competitors, String(id))).join(', ')
		}
		case 'aggregation_type':
			return i18n.t(String(value), { defaultValue: String(value) })
		default:
			return String(value)
	}
}

export const resolveParamValue = (
	param: CatalogParamModel,
	value: unknown,
	{ competitors = [], priceColumns = [] }: ParamValueOptions = {}
) => {
	if (value == null || value === '') return null

	switch (param.type) {
		case 'price_column':
			return labelFor(priceColumns, String(value))
		case 'boolean':
			return value as boolean
		case 'competitor_list': {
			const ids = Array.isArray(value) ? value : []
			if (!ids.length) return EMPTY

			return ids.map((id) => labelFor(competitors, String(id)))
		}
		case 'aggregation_type':
			return String(value)
		default:
			return value as number
	}
}
